// apps/dashboard/src/lib/api/student.ts
import { ApiError, fetchApi } from './client';
import { TestModule, Course } from '@/types';

export interface StudentCapabilities {
  tests: boolean;
  courses: boolean;
  certificates: boolean;
  platformCatalog: boolean;
}

export interface StudentContext {
  partner: {
    id: string;
    name: string;
    brandLogoUrl: string | null;
    canLeave: boolean;
  } | null;
  capabilities: StudentCapabilities;
}

/** A pending, unexpired roster invitation addressed to the caller's verified email. */
export interface StudentInvitation {
  id: string;
  partnerName: string;
  partnerLogoUrl: string | null;
  schoolName: string | null;
  invitedAt: string;
  expiresAt: string;
}

export type InvitationErrorCode =
  | 'NOT_FOUND'
  | 'INVITATION_NOT_PENDING'
  | 'PARTNER_NOT_ACTIVE'
  | 'PARTNER_FULL'
  // The bimbel suspended this person's existing membership; only the bimbel can lift it.
  | 'MEMBERSHIP_SUSPENDED';

const INVITATION_ERROR_CODES: readonly string[] = [
  'NOT_FOUND',
  'INVITATION_NOT_PENDING',
  'PARTNER_NOT_ACTIVE',
  'PARTNER_FULL',
  'MEMBERSHIP_SUSPENDED',
];

/** Narrows a thrown value to a known invitation error code; network and unlisted errors are UNKNOWN. */
export function getInvitationErrorCode(e: unknown): InvitationErrorCode | 'UNKNOWN' {
  if (e instanceof ApiError && e.code && INVITATION_ERROR_CODES.includes(e.code)) {
    return e.code as InvitationErrorCode;
  }
  return 'UNKNOWN';
}

export const studentApi = {
  getContext: (): Promise<StudentContext> => fetchApi('/student/context'),

  getPartnerModules: (): Promise<TestModule[]> =>
    fetchApi('/student/partner/modules'),

  // The enrolled partner's PUBLISHED courses (same shape as the public catalog).
  getPartnerCourses: (): Promise<Course[]> =>
    fetchApi('/student/partner/courses'),

  enroll: (code: string): Promise<{ partnerId: string }> =>
    fetchApi('/student/enroll', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  // Read-only lookup of a partner by join code (no enrollment side-effect).
  enrollPreview: (
    code: string,
  ): Promise<{ name: string; brandLogoUrl: string | null }> =>
    fetchApi(`/student/enroll/preview?code=${encodeURIComponent(code)}`),

  leave: (partnerId: string): Promise<{ ok: true }> =>
    fetchApi('/student/leave', {
      method: 'POST',
      body: JSON.stringify({ partnerId }),
    }),

  // Pending invitations for the signed-in user. An API without the endpoint yet
  // answers 404: `available: false` lets the page show a plain empty state instead
  // of telling people to check their email for an invitation that cannot exist.
  listInvitations: async (): Promise<{ items: StudentInvitation[]; available: boolean }> => {
    try {
      const res = await fetchApi<{ items: StudentInvitation[] }>('/student/invitations');
      return { items: res.items, available: true };
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return { items: [], available: false };
      throw e;
    }
  },

  // Reject with an ApiError whose `code` is an InvitationErrorCode (see getInvitationErrorCode).
  // `signal` lets the caller cut off a hung request; the server side is idempotent.
  acceptInvitation: (
    id: string,
    signal?: AbortSignal,
  ): Promise<{ status: 'ACCEPTED'; partnerId: string }> =>
    fetchApi(`/student/invitations/${encodeURIComponent(id)}/accept`, { method: 'POST', signal }),

  declineInvitation: (id: string, signal?: AbortSignal): Promise<{ status: 'DECLINED' }> =>
    fetchApi(`/student/invitations/${encodeURIComponent(id)}/decline`, { method: 'POST', signal }),
};
